import { Injectable } from '@angular/core';
import { DatabaseWrapper, DocWatcher, DocWatcherCallback } from 'services/database-wrapper';
import { PouchDoc } from '@cf-shared/data-model/pouch';
import { DeferredPromise } from '@cf-shared/shared-util';
import { CommonDbService } from './common-db.service';
import { LoggingService } from './logging.service';
import {
  ACTIVE_TEXT_SEGMENT,
  ENGLISH_TEXT_SEGMENT,
  HelpListReadCounts,
  TextLocaleSegment,
  helpDocIdForSegment,
  helpIdFromDocId,
  readHelpListWithEnglishFallbackAsync,
  readWithEnglishFallbackAsync,
} from './text-locale';


export interface TextEntry extends PouchDoc {
  title: string;
  text: string;
  dontShowInHelpPage?: boolean;
}

export namespace TextEntry {
  /**
   * The ENGLISH document id for a help text. English is the authoring locale:
   * the admin text editor creates and edits `en-us` documents, and the
   * translated siblings are generated into `databases/global_common.json` and
   * checked by the drift gate. Reads go through `TextDbService`, which picks the
   * active language and falls back here per document.
   */
  export function helpIdToPouchId(helpId: string) {
    return helpDocIdForSegment(helpId, ENGLISH_TEXT_SEGMENT);
  }

  /** The help id inside a document id of ANY locale. */
  export function pouchIdToHelpId(pouchId: string) {
    return helpIdFromDocId(pouchId);
  }
}
@Injectable()
export class TextDbService {
  databaseReady = DeferredPromise.defer<void>();

  constructor(private commonDb: CommonDbService, private logging: LoggingService) { }

  async initializeAsync() {
    // if commonDb is already open locally (warm resume Phase A), skip waiting
    // for remote connection — local PouchDB has all the text data
    if (!this.commonDb.database) {
      await this.commonDb.initializeAsync();
    }
    this.databaseReady.resolve(null);
  }

  async shutdownAsync() {
  }

  /**
   * Every help text, in the language being shown. Documents that language has
   * no translation for are served in English, so the list is never SHORTER for
   * a non-English user.
   *
   * Pass `segment` to pin one locale — the admin text editor pins English,
   * because English is the authoring locale.
   */
  async getAllTextsAsync(watcher?: DocWatcher, segment: TextLocaleSegment = ACTIVE_TEXT_SEGMENT): Promise<TextEntry[]> {
    await this.databaseReady;
    const readListForPrefixAsync = (activeWatcher: DocWatcher) => async (prefix: string) =>
      (await this.commonDb.database.getByIdHeadAsync(prefix, activeWatcher)) as TextEntry[];

    // every id here came from a `@help:` prefix query, so one that does not
    // parse is a corrupt document rather than a normal state. The merge keeps
    // the list usable and collects them here so they still reach Sentry.
    const malformedDocIds: string[] = [];
    // An id-prefix query used to answer an unavailable database with an empty
    // list, which made a failed read indistinguishable from "this language has
    // no documents" — this counter existed partly to notice that. It now throws
    // `DatabaseUnavailableError` instead, so that particular ambiguity is gone
    // and the throw propagates to the caller rather than arriving here as a
    // plausible-looking zero.
    //
    // The counter is still worth keeping: every help document now has a sibling
    // in every language, so a localized read that legitimately RAN and returned
    // nothing means the documents never replicated, which is a different fault
    // and one nothing else would report.
    const counts: HelpListReadCounts = { englishCount: 0, localizedCount: 0 };
    const reportAnyProblem = () => {
      if (malformedDocIds.length > 0) {
        this.logging.trackSilentProblem('HelpTextMalformedDocId', {
          count: malformedDocIds.length,
          docIds: malformedDocIds.slice(0, 10).join(', '),
        });
      }
      if (segment !== ENGLISH_TEXT_SEGMENT && counts.localizedCount === 0 && counts.englishCount > 0) {
        this.logging.trackSilentProblem('HelpTextLocalizedListEmpty', {
          segment,
          englishCount: counts.englishCount,
        });
      }
    };

    if (watcher) {
      try {
        return await readHelpListWithEnglishFallbackAsync(
          readListForPrefixAsync(watcher), segment, malformedDocIds, counts);
      } finally {
        reportAnyProblem();
      }
    }
    try {
      return await this.commonDb.database.withDocWatcherAsync(tempWatcher => readHelpListWithEnglishFallbackAsync(
        readListForPrefixAsync(tempWatcher), segment, malformedDocIds, counts));
    } finally {
      reportAnyProblem();
    }
  }

  // Create a persistent reactive watcher on the shared text/common DB.
  // Callers pass the returned watcher to getTextByHelpIdAsync so a later
  // sync of the watched doc re-fires the callback. Keeps commonDb access
  // encapsulated here (mirrors config-db.service.ts owning its own
  // commonDb watcher) instead of having UI components reach into commonDb.
  // The caller owns the lifecycle and must call watcher.destroy() on teardown.
  createTextWatcher(callback: DocWatcherCallback): DocWatcher {
    return this.commonDb.database.createDocWatcher(callback);
  }

  /**
   * Registers one help text's document, in one locale, on a watcher without reading it.
   *
   * A read registers its document only after it succeeds, so a read that throws leaves
   * the watcher watching nothing. Call this BEFORE the read when a caller that catches
   * the failure must still be woken once the document arrives (#6245), the same way
   * `getSingleton` registers before it refuses.
   */
  watchTextByHelpId(helpId: string, watcher: DocWatcher, segment: TextLocaleSegment): void {
    this.commonDb.database.registerDocWithWatcher(helpDocIdForSegment(helpId, segment), watcher);
  }

  /**
   * One help text in the language being shown, falling back to the English
   * document when that language has no sibling for it (see
   * `readWithEnglishFallbackAsync` — a missing translation is an expected
   * state, not an error).
   *
   * Pass `segment` to pin one locale — the admin text editor pins English,
   * because English is the authoring locale.
   */
  async getTextByHelpIdAsync(
    helpId: string,
    watcher?: DocWatcher,
    segment: TextLocaleSegment = ACTIVE_TEXT_SEGMENT,
  ): Promise<TextEntry> {
    await this.databaseReady;
    const readDocAsync = (activeWatcher: DocWatcher) => (id: string) =>
      this.commonDb.database.getTypedRecordAsync<TextEntry>(id, activeWatcher);
    const buildId = (forSegment: TextLocaleSegment) => helpDocIdForSegment(helpId, forSegment);

    if (watcher) {
      return readWithEnglishFallbackAsync(buildId, readDocAsync(watcher), segment);
    }
    return this.commonDb.database.withDocWatcherAsync(
      tempWatcher => readWithEnglishFallbackAsync(buildId, readDocAsync(tempWatcher), segment));
  }

  async saveTextAsync(t: TextEntry) {
    await this.commonDb.database.saveRecordAsync(t, DatabaseWrapper.getTransactionId());
  }

  async deleteTextAsync(t: TextEntry) {
    await this.commonDb.database.deleteRecordAsync(t, DatabaseWrapper.getTransactionId());
  }
}
